import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyTalentNetworks,
  submitTalentNetwork,
  type TalentNetworkSubmitInput,
} from '../lib/talent-network-api';
import { fetchPublicCompanyPage } from '../lib/public-profiles-api';

/** The candidate's own memberships (GET /api/talent-network/mine). */
export const MY_TALENT_NETWORKS_KEY = ['talent-network', 'mine'] as const;

/** The public company page behind a /company-join/<slug> handoff. */
export function usePublicCompanyPage(slug: string, enabled = true) {
  return useQuery({
    queryKey: ['public-company-page', slug],
    queryFn: () => fetchPublicCompanyPage(slug),
    enabled: enabled && Boolean(slug),
    staleTime: 60_000,
  });
}

/**
 * The talent networks the candidate joined, with what each one received
 * (My Jobs and the membership detail, audit E03).
 */
export function useMyTalentNetworks(enabled = true) {
  return useQuery({
    queryKey: MY_TALENT_NETWORKS_KEY,
    queryFn: fetchMyTalentNetworks,
    enabled,
    staleTime: 30_000,
    // My Jobs waits on this list only when it has no applications to show.
    retry: 1,
  });
}

/** Join a company's talent network (connect-join dialog submit, PRD §8.5). */
export function useSubmitTalentNetwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TalentNetworkSubmitInput) => submitTalentNetwork(input),
    onSuccess: () => {
      // A new or resubmitted membership has a new receipt to show.
      void queryClient.invalidateQueries({ queryKey: MY_TALENT_NETWORKS_KEY });
    },
  });
}
